import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home as HomeIcon, Waves, Trees, Sofa, ChefHat, Music, Flame,
  UserCheck, Shield, Wifi, Star, MapPin, Phone, MessageCircle,
  ArrowUpRight, Plus, AlertTriangle,
  Rocket, UtensilsCrossed, ShieldCheck, ExternalLink,
} from 'lucide-react';
import { farmhouseData } from '../data/mockData';
import { byslug } from '../data/media';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import Marquee from '../components/Marquee';
import Reveal from '../components/Reveal';
import ParallaxHero from '../components/ParallaxHero';
import PhotoCorridor from '../components/PhotoCorridor';
import ContainerModel from '../components/ContainerModel';
import TiltCard from '../components/TiltCard';
import EmberField from '../components/EmberField';
import AvailabilityCalendar from '../components/AvailabilityCalendar';
import useSEO from '../hooks/useSEO';
import '../styles/studio.css';
import '../styles/night.css';

const iconMap = {
  Home: HomeIcon, Waves, Trees, Sofa, ChefHat, Music, Flame,
  UserCheck, Shield, Wifi,
};

const pad2 = (n) => String(n).padStart(2, '0');

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: farmhouseData.faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: { '@type': 'Answer', text: faq.answer },
  })),
};

// Alternates night exteriors with the daytime pool/interior set so the
// scroll actually moves through the property instead of circling the lawn.
const fromGallery = (title, caption) => {
  const found = farmhouseData.gallery.find((g) => g.title === title);
  return found ? { slug: found.url, src: found.url, caption } : null;
};

const CORRIDOR = [
  byslug('villa-night-full'),
  fromGallery('Private Pool & Container House', 'The private pool'),
  fromGallery('Elegant Living Room', 'Living room'),
  byslug('container-detail'),
  fromGallery('Master Bedroom', 'Master bedroom'),
  byslug('fire-blaze'),
  fromGallery('Outdoor Terrace Seating', 'Terrace seating'),
  byslug('entry-stair'),
  fromGallery('Dining Area', 'Dining area'),
  fromGallery('Rooftop Terrace', 'Rooftop deck'),
  byslug('villa-palms'),
  fromGallery('Cozy Bedroom', 'Second bedroom'),
  fromGallery('Daytime Exterior View', 'The villa by day'),
].filter(Boolean);

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Keep these in step with the static tags in public/index.html — this hook
  // overwrites them once React mounts, and Google reads the post-render values.
  useSEO({
    title: 'Private Pool Farmhouse for Rent in Hyderabad | Leoland, Shamirpet',
    description: '4BHK farmhouse with a private pool at Shamirpet, 5 min off the Hyderabad ORR. Sleeps 12, caretaker, bonfire & BBQ. From \u20b912,000 a night — book direct.',
    path: '/',
  });

  useEffect(() => {
    const targetId = location.state && location.state.scrollTo;
    if (targetId) {
      const timer = setTimeout(() => scrollTo(targetId), 120);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.state]);

  const bonfire = byslug('fire-pit');

  return (
    <div className="studio">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Nav />

      <ParallaxHero
        onBook={() => scrollTo('availability')}
        onGallery={() => navigate('/gallery')}
      />

      <Marquee
        className="surface-accent"
        items={['Private Pool', '4 Bedrooms', 'Sleeps 12', 'Bonfire & BBQ', 'Leonia Resorts, Shamirpet', 'Book Direct']}
      />

      {/* ---------- Intro ---------- */}
      <section className="surface-paper section" id="about">
        <div className="edge wrap">
          <Reveal>
            <div className="manifesto-grid">
              <div>
                <span className="folio">01 &mdash; The Property</span>
                <p className="manifesto-statement" style={{ marginTop: '1.25rem' }}>
                  A private 4BHK farmhouse near Hyderabad &mdash; container
                  volumes wrapped in glass and lit from the inside, set on a
                  lawn inside Leonia Resorts, five minutes off the ORR at
                  Shamirpet.
                </p>
              </div>
              <div className="manifesto-aside">
                <p className="copy">
                  Four air-conditioned bedrooms sleep twelve, which makes it an
                  easy pick for a family weekend, a birthday or a small
                  get-together. Downstairs opens onto the garden and private
                  pool; upstairs is a deck you&rsquo;ll end up on well past
                  midnight. A full-time caretaker stays on site, and District
                  Gravity &mdash; 150+ activities &mdash; is a two-minute walk
                  from the gate.
                </p>
                <div style={{ display: 'flex', gap: '2.5rem', flexWrap: 'wrap' }}>
                  {[
                    ['Sleeps', '12 guests'],
                    ['Bedrooms', '4 BHK'],
                    ['Pool', 'Private'],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="mono-tag">{k}</div>
                      <div style={{ marginTop: '0.35rem', color: 'var(--glow)', fontWeight: 600, fontFamily: 'var(--font-display)', fontSize: '1.15rem' }}>
                        {v}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- 3D scroll gallery ---------- */}
      <PhotoCorridor images={CORRIDOR} />

      <div className="surface-paper edge" style={{ paddingBottom: 'clamp(3rem,7vw,5rem)' }}>
        <div className="wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
          <span className="folio">Shot on location, March 2026</span>
          <button className="btn btn-line-dark" onClick={() => navigate('/gallery')}>
            View full gallery <ArrowUpRight size={15} />
          </button>
        </div>
      </div>

      {/* ---------- The building (3D model) ---------- */}
      <section className="surface-paper-dim section">
        <div className="edge wrap">
          <div className="feature-split">
            <Reveal>
              <span className="label">02 &mdash; The Build</span>
              <h2 className="display-2" style={{ marginTop: '0.9rem' }}>
                Turn it around.
              </h2>
              <p className="copy" style={{ marginTop: '1.2rem', maxWidth: '46ch' }}>
                Roughly 24 by 30 feet. A central core holds the entrance,
                kitchen and washroom, with the two bedroom wings splayed off it
                at an angle so each one looks out over its own stretch of
                garden. A pergola shades the entrance deck; the pool sits off
                the near side.
              </p>
              <div className="hero-specs" style={{ marginTop: '2rem' }}>
                <div className="hero-spec">
                  <div className="hero-spec-value" style={{ color: 'var(--glow)' }}>24&times;30</div>
                  <div className="hero-spec-label">Feet</div>
                </div>
                <div className="hero-spec">
                  <div className="hero-spec-value" style={{ color: 'var(--glow)' }}>4</div>
                  <div className="hero-spec-label">Bedrooms</div>
                </div>
                <div className="hero-spec">
                  <div className="hero-spec-value" style={{ color: 'var(--glow)' }}>01</div>
                  <div className="hero-spec-label">Private pool</div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={1}>
              <ContainerModel />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- Amenities ---------- */}
      <section className="surface-paper section" id="specifications">
        <div className="edge wrap">
          <Reveal className="section-head">
            <div>
              <span className="label">03 &mdash; What&rsquo;s Included</span>
              <h2 className="display-2" style={{ marginTop: '0.9rem' }}>Everything, included</h2>
            </div>
            <p className="copy" style={{ maxWidth: '360px' }}>
              The whole property is yours &mdash; no add-on packages, no fine print.
            </p>
          </Reveal>

          <div className="amenity-tiles">
            {farmhouseData.amenities.map((amenity, index) => {
              const Icon = iconMap[amenity.icon];
              return (
                <TiltCard className="amenity-tile" key={amenity.title}>
                  <div>
                    <span className="idx">{pad2(index + 1)}</span>
                    <div className="ico" style={{ marginTop: '1rem' }}>
                      {Icon && <Icon size={24} />}
                    </div>
                  </div>
                  <div>
                    <h3>{amenity.title}</h3>
                    <p>{amenity.description}</p>
                  </div>
                </TiltCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Bonfire band ---------- */}
      <section className="bonfire-band">
        <picture>
          <source srcSet={`${bonfire.webpSmall} 640w, ${bonfire.webp} 1200w, ${bonfire.webpLarge} 2400w`} type="image/webp" sizes="100vw" />
          <img src={bonfire.src} alt="Bonfire pit lit at Leoland by VengaFarms" loading="lazy" />
        </picture>
        <div className="scrim" aria-hidden="true" />
        <EmberField density={26} />
        <div className="inner edge">
          <div className="wrap">
            <Reveal>
              <span className="label">Evenings</span>
              <h2 className="display-2" style={{ marginTop: '0.9rem', maxWidth: '14ch' }}>
                The fire gets lit at eight.
              </h2>
              <p className="copy" style={{ marginTop: '1.1rem', maxWidth: '44ch' }}>
                Bonfire and BBQ are &#8377;1,000 each, charcoal and supplies
                included. Tell the caretaker in advance and it&rsquo;s lit when
                you are.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- Rates ---------- */}
      <section className="surface-paper section" id="rates">
        <div className="edge wrap">
          <Reveal className="section-head">
            <div>
              <span className="label">04 &mdash; Rates</span>
              <h2 className="display-2" style={{ marginTop: '0.9rem' }}>Straightforward pricing</h2>
            </div>
            <p className="copy" style={{ maxWidth: '360px' }}>
              One price for the whole villa. No hidden charges.
            </p>
          </Reveal>

          <Reveal delay={1}>
            <div className="plaque-grid">
              <div className="plaque">
                <span className="plaque-tag">Weekday</span>
                <div className="plaque-name">Sunday &ndash; Friday</div>
                <div className="plaque-amount">
                  &#8377;{farmhouseData.pricing.weekday.toLocaleString()}<span className="unit">/ night</span>
                </div>
                <ul className="plaque-features">
                  <li>Up to 12 guests</li>
                  <li>All amenities included</li>
                  <li>Full-time caretaker</li>
                  <li>Free WiFi &amp; parking</li>
                </ul>
                <button className="btn btn-line-dark btn-block" onClick={() => scrollTo('availability')}>
                  Book weekday
                </button>
              </div>

              <div className="plaque is-accent">
                <span className="plaque-tag">Most popular</span>
                <div className="plaque-name">Saturday</div>
                <div className="plaque-amount">
                  &#8377;{farmhouseData.pricing.saturday.toLocaleString()}<span className="unit">/ night</span>
                </div>
                <ul className="plaque-features">
                  <li>Up to 12 guests</li>
                  <li>All amenities included</li>
                  <li>Full-time caretaker</li>
                  <li>Free WiFi &amp; parking</li>
                  <li>Prime weekend slot</li>
                </ul>
                <button className="btn btn-glow btn-block" onClick={() => scrollTo('availability')}>
                  Book Saturday
                </button>
              </div>
            </div>

            <div className="plaque-strip">
              {[
                ['Check-in', farmhouseData.pricing.checkIn],
                ['Check-out', farmhouseData.pricing.checkOut],
                ['Max guests', `${farmhouseData.pricing.maxGuests} people`],
              ].map(([k, v]) => (
                <div className="plaque-strip-item" key={k}>
                  <div className="mono-tag" >{k}</div>
                  <div className="display-3" style={{ marginTop: '0.4rem' }}>{v}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Availability (the bright moment) ---------- */}
      <section className="surface-paper section" id="availability">
        <AvailabilityCalendar />
      </section>

      <section className="surface-paper-dim section-tight">
        <div className="edge wrap">
          <Reveal style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <span className="label">Direct booking only</span>
              <h3 className="display-3" style={{ marginTop: '0.75rem' }}>Book with us directly</h3>
              <p className="copy" style={{ marginTop: '0.6rem', maxWidth: '44ch' }}>
                No platform fees, no middlemen &mdash; message or call and
                we&rsquo;ll confirm your dates.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <a href="https://wa.me/918008002388?text=Hi,%20I'm%20interested%20in%20booking%20Leoland%20by%20VengaFarms" target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp">
                <MessageCircle size={15} /> WhatsApp us
              </a>
              <a href="tel:+918008002388" className="btn btn-solid-dark">
                <Phone size={15} /> +91 80080 02388
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Reviews ---------- */}
      <section className="surface-paper-dim section" id="testimonials">
        <div className="edge wrap">
          <Reveal className="section-head">
            <div>
              <span className="label">05 &mdash; Guest Stories</span>
              <h2 className="display-2" style={{ marginTop: '0.9rem' }}>Reviews</h2>
            </div>
          </Reveal>
          <div className="amenity-tiles">
            {farmhouseData.testimonials.map((t, index) => (
              <TiltCard className="amenity-tile" key={t.name} style={{ minHeight: '260px' }}>
                <div>
                  <span className="idx">{pad2(index + 1)}</span>
                  <div style={{ display: 'flex', gap: '0.2rem', margin: '1rem 0' }}>
                    {[...Array(t.rating)].map((_, i) => (
                      <Star key={i} size={13} fill="#ffbe72" color="#ffbe72" />
                    ))}
                  </div>
                  <p style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '1.05rem', lineHeight: 1.45, color: 'var(--text-dark-1)' }}>
                    &ldquo;{t.text}&rdquo;
                  </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--line-dark-soft)' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{t.name}</span>
                  <span className="mono-tag">{t.date}</span>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Getting here ---------- */}
      <section className="surface-paper section" id="contact">
        <div className="edge wrap">
          <Reveal className="section-head">
            <div>
              <span className="label">06 &mdash; Find Us</span>
              <h2 className="display-2" style={{ marginTop: '0.9rem' }}>Getting here</h2>
            </div>
            <p className="copy" style={{ maxWidth: '360px' }}>
              Inside Leonia Resorts, Shamirpet &mdash; five minutes from the ORR exit.
            </p>
          </Reveal>

          <Reveal delay={1} className="step-grid">
            {[
              ['Step 01', 'Leonia Resorts', 'Navigate to the Leonia Resorts main gate first.', farmhouseData.location.leoniaResortsMapUrl, 'Open Leonia map'],
              ['Step 02', 'Venga Farms', 'Once inside the resort, navigate to Plot 429.', farmhouseData.location.vengaFarmsMapUrl, 'Open farm map'],
            ].map(([step, title, copy, url, cta]) => (
              <div className="step-card" key={step}>
                <div className="step-num" style={{ color: 'var(--glow)' }}>{step}</div>
                <div className="step-title">{title}</div>
                <p className="copy" style={{ marginBottom: '1.5rem' }}>{copy}</p>
                <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-line-dark">
                  {cta} <ExternalLink size={14} />
                </a>
              </div>
            ))}
          </Reveal>

          <Reveal delay={2} className="notice-panel">
            <AlertTriangle size={22} className="notice-icon" />
            <div>
              <div className="display-3" style={{ fontSize: '1.05rem', marginBottom: '0.5rem' }}>
                Important navigation note
              </div>
              <p className="copy" style={{ marginBottom: '0.75rem' }}>
                {farmhouseData.location.directions}
              </p>
              <p className="mono-tag" style={{ color: 'var(--glow)' }}>
                <MapPin size={13} style={{ verticalAlign: '-2px', marginRight: '0.35rem' }} />
                {farmhouseData.location.gateEntry}
              </p>
            </div>
          </Reveal>

          <Reveal delay={3} className="highlight-strip">
            {[
              [Rocket, '2-minute walk', 'District Gravity', '150+ activities: adventure sports, zip-lining, rock climbing.'],
              [UtensilsCrossed, 'Multiple options', 'Food & dining', 'Leonia food court, Swiggy & Zomato delivery, nearby dhabas.'],
              [ShieldCheck, '24/7 security', 'Safe & accessible', 'Inside Leonia Resorts boundaries, 5 minutes from the ORR exit.'],
            ].map(([Icon, tag, title, copy]) => (
              <div className="highlight-strip-item" key={title}>
                <span className="highlight-strip-tag" style={{ color: 'var(--glow)' }}>
                  <Icon size={14} style={{ verticalAlign: '-2px', marginRight: '0.4rem' }} />{tag}
                </span>
                <div className="highlight-strip-title">{title}</div>
                <p className="copy">{copy}</p>
              </div>
            ))}
          </Reveal>

          <Reveal delay={4} className="contact-grid-studio">
            <div className="contact-cell">
              <div className="contact-cell-label">Caretaker &mdash; {farmhouseData.location.caretaker.name}</div>
              <a href={`tel:${farmhouseData.location.caretaker.phone}`} className="contact-cell-value">
                {farmhouseData.location.caretaker.phone}
              </a>
            </div>
            <div className="contact-cell">
              <div className="contact-cell-label">Booking enquiries</div>
              <a href="tel:+918008002388" className="contact-cell-value">+91 80080 02388</a>
            </div>
            <div className="contact-cell">
              <div className="contact-cell-label">WiFi</div>
              <span className="contact-cell-value">Shared at check-in</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- House rules ---------- */}
      <section className="surface-paper-dim section" id="rules">
        <div className="edge wrap">
          <Reveal className="section-head">
            <div>
              <span className="label">07 &mdash; Good to Know</span>
              <h2 className="display-2" style={{ marginTop: '0.9rem' }}>House rules</h2>
            </div>
          </Reveal>
          <Reveal delay={1}>
            <ul className="rules-list">
              {farmhouseData.houseRules.map((rule, index) => (
                <li key={rule}>
                  <span className="rule-num" style={{ color: 'var(--glow)' }}>{pad2(index + 1)}</span>
                  <span>{rule}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section className="surface-paper section" id="faq">
        <div className="edge wrap">
          <Reveal className="section-head">
            <div>
              <span className="label">08 &mdash; Questions</span>
              <h2 className="display-2" style={{ marginTop: '0.9rem' }}>Frequently asked</h2>
            </div>
          </Reveal>

          <Reveal delay={1} className="faq-list-studio">
            {farmhouseData.faqs.map((faq, index) => {
              const isOpen = expandedFaq === index;
              return (
                <div key={faq.question} className={`faq-row ${isOpen ? 'open' : ''}`}>
                  <div className="faq-row-head" onClick={() => setExpandedFaq(isOpen ? null : index)}>
                    <span className="faq-row-q">
                      <span className="faq-row-num" style={{ color: 'var(--glow)' }}>{pad2(index + 1)}</span>
                      {faq.question}
                    </span>
                    <Plus size={18} className="faq-row-icon" />
                  </div>
                  <div className="faq-row-body-wrap">
                    <div className="faq-row-body-inner">
                      <p className="faq-row-body">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </Reveal>
        </div>
      </section>

      <Footer />

      <a
        href="https://wa.me/918008002388?text=Hi,%20I'm%20interested%20in%20booking%20Leoland%20by%20VengaFarms"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle size={26} color="white" />
      </a>
    </div>
  );
};

export default Home;
